// @ts-nocheck
import {
  projectUrls,
  taskBaseUrl,
  relationsBaseUrl,
  gridBaseUrl,
  settingsViewFrontend,
} from '../apiUrls';
import type { Dispatch } from 'redux';
import axios from '../../../../services/api';
import { ProjectAction, ProjectActions } from '../types/project';
import { IChangeTaskPhaseInput } from '../models/ChangeTaskPhaseInput ';
import { IChangeProjectPhaseOrderInput } from '../models/ChangeProjectPhaseOrderInput';
import { IChangeTaskDurationAndPriorityInput } from '../models/ChangeTaskDurationAndPriorityInput';
import { ICreateTaskInput } from '../models/CreateTaskInput';
import { ICreateProjectPhaseInput } from '../models/ICreateProjectPhaseInput';
import { StatusCode } from '../models/StatusCode';
import { IIdValueModel } from '../models/IIdValueModel';
import { IIdModel } from '../models/IIdModel';
import { FrontendVisibility } from '../models/ISettingsFrontend';
import { TaskPriority } from '../models/TaskPriority';

const projectRequestsInFlight = new Map<number, Promise<void>>();

export const fetchCurrentProjects = (
  allProjects = false,
  silent = false,
  showEndedProjects = false
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      if (!silent) {
        dispatch({ type: ProjectActions.FETCH_CURRENT_PROJECTS });
      }
      const response = await axios.get(projectUrls.getAssignedProjects, {
        params: { allProjects, showEndedProjects },
      });

      dispatch({
        type: silent
          ? ProjectActions.FETCH_CURRENT_PROJECTS_SILENT_SUCCESS
          : ProjectActions.FETCH_CURRENT_PROJECTS_SUCCESS,
        payload: response.data.data,
      });
    } catch (e) {
      dispatch({
        type: silent
          ? ProjectActions.FETCH_CURRENT_PROJECTS_SILENT_ERROR
          : ProjectActions.FETCH_CURRENT_PROJECTS_ERROR,
        payload: `ERROR: Can't get project tasks`,
      });
    }
  };
};

export const fetchProject = (projectId: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    const existingRequest = projectRequestsInFlight.get(projectId);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async () => {
      try {
        dispatch({ type: ProjectActions.FETCH_PROJECT });
        const response = await axios.get(projectUrls.getProjectById, {
          params: { projectId: projectId },
        });
        dispatch({
          type: ProjectActions.FETCH_PROJECT_SUCCESS,
          payload: response.data.data,
        });
      } catch (e) {
        dispatch({
          type: ProjectActions.FETCH_PROJECT_ERROR,
          payload: `ERROR: Can't get project with ${projectId} id`,
        });
      }
    })();

    projectRequestsInFlight.set(projectId, request);
    try {
      await request;
    } finally {
      if (projectRequestsInFlight.get(projectId) === request) {
        projectRequestsInFlight.delete(projectId);
      }
    }
  };
};

export const fetchProjectPhases = (projectId: number, projectReleaseId?: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({
        type: ProjectActions.SET_CURRENT_PROJECT_RELEASE,
        payload: { projectReleaseId },
      });
      dispatch({ type: ProjectActions.FETCH_PROJECT_PHASES });
      const response = await axios.get(projectUrls.baseUrl + 'GetProjectPhasesByProjectId', {
        params: { projectId: projectId, projectReleaseId: projectReleaseId },
      });
      dispatch({
        type: ProjectActions.FETCH_PROJECT_PHASES_SUCCESS,
        payload: response.data.data,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.FETCH_PROJECT_PHASES_ERROR,
        payload: `ERROR: Can't get phases for project with ${projectId} id`,
      });
    }
  };
};
export const fetchProjectArchivePhase = (projectId: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.FETCH_PROJECT_ARCHIVE_PHASE });
      const response = await axios.get(projectUrls.baseUrl + 'GetArchiveTaskByProjectId', {
        params: { projectId: projectId },
      });
      dispatch({
        type: ProjectActions.FETCH_PROJECT_ARCHIVE_PHASE_SUCCESS,
        payload: response.data.data,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.FETCH_PROJECT_ARCHIVE_PHASE_ERROR,
        payload: `ERROR: Can't get archive phases for project with ${projectId} id`,
      });
    }
  };
};
export const setProjectArchivePhase = (projectId: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({
        type: ProjectActions.SET_PROJECT_LOADER,
        payload: true,
      });
      // DOM needs to be rendered
      await new Promise((resolve) => setTimeout(resolve, 0));
      dispatch({
        type: ProjectActions.SET_PROJECT_ARCHIVE_PHASE,
      });
      dispatch({
        type: ProjectActions.SET_PROJECT_LOADER,
        payload: false,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.FETCH_PROJECT_PHASES_ERROR,
        payload: `ERROR: Can't set archive phases for project with ${projectId} id`,
      });
    }
  };
};
export const removeProjectArchivePhase = (projectId: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({
        type: ProjectActions.SET_PROJECT_LOADER,
        payload: true,
      });
      // DOM needs to be rendered
      await new Promise((resolve) => setTimeout(resolve, 0));
      dispatch({
        type: ProjectActions.REMOVE_PROJECT_ARCHIVE_PHASE,
      });
      dispatch({
        type: ProjectActions.SET_PROJECT_LOADER,
        payload: false,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.FETCH_PROJECT_PHASES_ERROR,
        payload: `ERROR: Can't remove archive phases for project with ${projectId} id`,
      });
    }
  };
};
export const fetchDevelopers = (nodeKey: string, projectId: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      const response = await axios.post(
        gridBaseUrl + 'GetGridDataWithParametersAsync?nodeKey=' + nodeKey,
        {
          ProjectId: projectId,
        }
      );
      let developers = Array.isArray(response.data) ? response.data : [];

      try {
        const employeesResponse = await axios.get(gridBaseUrl + 'GetAllEmployees');
        const employees = employeesResponse.data?.data ?? employeesResponse.data;
        const employeesById = new Map(
          (Array.isArray(employees) ? employees : []).map((employee: any) => [
            String(employee.Id ?? employee.id),
            employee,
          ])
        );
        developers = developers.map((developer: any) => {
          const employee = employeesById.get(
            String(developer.crmRelationId ?? developer.CrmRelationId)
          );
          return {
            ...developer,
            crmRelationInitials:
              employee?.Initials ?? employee?.initials ?? developer.crmRelationInitials ?? null,
          };
        });
      } catch (error) {
        console.warn('[Kanban] Failed to enrich developers with initials.', error);
      }

      dispatch({
        type: ProjectActions.FETCH_DEVELOPERS_SUCCESS,
        payload: developers,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.FETCH_DEVELOPERS_ERROR,
        payload: `ERROR: Can't get developers for project with ${projectId} id`,
      });
    }
  };
};

export const sort = (
  droppableIdStart: string,
  droppableIdEnd: string,
  droppableIndexStart: number,
  droppableIndexEnd: number,
  draggableId: string
) => {
  return (dispatch: Dispatch<ProjectAction>) => {
    dispatch({
      type: ProjectActions.DRAG_HAPPEND,
      payload: {
        droppableIdStart,
        droppableIdEnd,
        droppableIndexStart,
        droppableIndexEnd,
        draggableId,
      },
    });
  };
};

export const sortListOrder = (projectId: number, indexStart: number, indexEnd: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.CHANGE_PROJECT_PHASE_ORDER });
      const response = await axios.post(projectUrls.changePhaseOrder, {
        projectId,
        indexStart,
        indexEnd,
      } as IChangeProjectPhaseOrderInput);

      if (response.data?.Code !== undefined && response.data.Code !== 1) {
        throw new Error(response.data?.data?.Error || 'Unable to change project phase order');
      }

      dispatch({
        type: ProjectActions.DRAG_LIST_HAPPEND,
        payload: {
          projectId,
          indexStart,
          indexEnd,
        },
      });
      return true;
    } catch (e) {
      dispatch({
        type: ProjectActions.CHANGE_PROJECT_PHASE_ORDER_ERROR,
        payload: `ERROR: Can't change project phase order`,
      });
      return false;
    }
  };
};

export const changeTaskPhase = (
  droppableIdStart: string,
  droppableIdEnd: string,
  droppableIndexStart: number,
  droppableIndexEnd: number,
  draggableId: string,
  selectedRelease: number | null,
  sortingType: number
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({
        type: ProjectActions.UPDATE_TASK_PHASE,
        payload: {
          droppableIdStart,
          droppableIdEnd,
          droppableIndexStart,
          droppableIndexEnd,
          draggableId,
          selectedRelease,
          sortingType,
        },
      });

      dispatch({ type: ProjectActions.CHANGE_TASK_PHASE });

      const requestBody = {
        taskId: Number(draggableId),
        taskPhaseId: Number(droppableIdEnd),
        taskPosition: Number(droppableIndexEnd),
        selectedReleaseId: Number(selectedRelease),
        sortingType,
      } as IChangeTaskPhaseInput;

      const response = await axios.post(taskBaseUrl + 'ChangeTaskPhaseAsync', requestBody);

      dispatch({
        type: ProjectActions.CHANGE_TASK_PHASE_SUCCESS,
        payload: response.data.data,
      });

      return true;
    } catch (e) {
      console.error('[Redux Thunk] Error changing task phase:', e);
      dispatch({
        type: ProjectActions.CHANGE_TASK_PHASE_ERROR,
        payload: `ERROR: Can't change task phase`,
      });

      return false;
    }
  };
};

export const restoreTaskPhaseMove = (payload: { project: any; board: any }) => {
  return (dispatch: Dispatch<ProjectAction>) => {
    dispatch({
      type: ProjectActions.RESTORE_TASK_PHASE_MOVE,
      payload,
    });
  };
};

export const changeTaskDurationAndPriority = (
  taskId: number,
  taskStart: Date,
  taskDueDate: Date,
  priorityId: number
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.CHANGE_TASK_DURATION_AND_PRIORITY });
      const response = await axios.post(taskBaseUrl + 'ChangeTaskDurationAndPriority', {
        taskId: taskId,
        taskStart: taskStart.toLocaleString('en-US'),
        taskDueDate: taskDueDate.toLocaleString('en-US'),
        priorityId: priorityId,
      } as IChangeTaskDurationAndPriorityInput);
      dispatch({
        type: ProjectActions.CHANGE_TASK_DURATION_AND_PRIORITY_SUCCESS,
        payload: response.data.data,
      });
      return true;
    } catch (e) {
      console.error("[Redux Thunk] Error changing task's due date:", e);
      dispatch({
        type: ProjectActions.CHANGE_TASK_DURATION_AND_PRIORITY_ERROR,
        payload: `ERROR: Can't change task's duration and priority`,
      });
      return false;
    }
  };
};

export const createMultipleTasks = (nodeKey: string, tasks: ICreateTaskInput[]) => {
  return async (dispatch: Dispatch<ProjectAction>, getState?: () => any) => {
    try {
      dispatch({ type: ProjectActions.CREATE_MULTIPLE_TASKS });

      const state = typeof getState === 'function' ? getState() : {};
      const modalParameters = state?.modal?.modalParameters ?? null;
      const currentProjectReleaseId = state?.project?.currentProjectReleaseId ?? null;

      const tasksToSend = (tasks || []).map((t: any) => {
        const task = { ...(t || {}) } as any;

        const isUnsetOrZero = (v: any) => v == null || v === 0;
        const setIfUnsetOrZero = (key: string, value: any) => {
          if (isUnsetOrZero(task[key]) && value !== null && value !== undefined) {
            task[key] = Number(value);
          }
        };

        setIfUnsetOrZero('projectId', modalParameters?.projectId);
        setIfUnsetOrZero('taskPhaseId', modalParameters?.taskPhaseId);

        if (isUnsetOrZero(task.priorityId)) {
          task.priorityId = TaskPriority.Medium;
        }

        if (task.projectReleasesId == null && currentProjectReleaseId) {
          task.projectReleasesId = Number(currentProjectReleaseId);
        }

        return task as ICreateTaskInput;
      });

      const response = await axios.post(
        taskBaseUrl + `CreateMultipleTasks?nodeKey=${nodeKey}`,
        tasksToSend
      );

      dispatch({
        type: ProjectActions.CREATE_MULTIPLE_TASKS_SUCCESS,
        payload: response.data.data,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.CREATE_MULTIPLE_TASKS_ERROR,
        payload: `ERROR: Can't create multiple tasks.`,
      });
    }
  };
};

export const archiveAllPhaseTasks = (
  projectId: number | string,
  phaseId: number | string,
  projectReleaseId?: number | string
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.ARCHIVE_ALL_PHASE_TASKS });
      const response = await axios.post(taskBaseUrl + `ArchiveAllPhaseTasksAsync`, null, {
        params: {
          projectId,
          phaseId,
          projectReleaseId,
        },
      });

      if (response.data.Code === StatusCode.Success) {
        dispatch({
          type: ProjectActions.ARCHIVE_ALL_PHASE_TASKS_SUCCESS,
          payload: { phaseId },
        });
      } else {
        dispatch({
          type: ProjectActions.ARCHIVE_ALL_PHASE_TASKS_ERROR,
          payload: response.data.data.Error,
        });
      }
    } catch (e) {
      dispatch({
        type: ProjectActions.ARCHIVE_ALL_PHASE_TASKS_ERROR,
        payload: `ERROR: Can't archive all phase tasks.`,
      });
    }
  };
};

export const moveToReleaseAllPhaseTasks = (
  projectId: string | number,
  phaseId: string | number,
  projectReleaseIdNew: string | number,
  projectReleaseIdCurrent?: string | number
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.MOVE_TO_RELEASE_ALL_PHASE_TASKS });
      const response = await axios.post(taskBaseUrl + `MoveToReleaseAllPhaseTasksAsync`, null, {
        params: {
          projectId,
          phaseId,
          projectReleaseIdNew,
          projectReleaseIdCurrent,
        },
      });

      if (response.data.Code === StatusCode.Success) {
        dispatch({
          type: ProjectActions.MOVE_TO_RELEASE_ALL_PHASE_TASKS_SUCCESS,
          payload: { phaseId, projectReleaseIdCurrent, projectReleaseIdNew },
        });
      } else {
        dispatch({
          type: ProjectActions.MOVE_TO_RELEASE_ALL_PHASE_TASKS_ERROR,
          payload: response.data.data.Error,
        });
      }
    } catch (e) {
      dispatch({
        type: ProjectActions.MOVE_TO_RELEASE_ALL_PHASE_TASKS_ERROR,
        payload: `ERROR: Can't move all phase tasks.`,
      });
    }
  };
};

export const moveToPhaseAllTasks = (
  projectId: string | number,
  phaseIdNew: string | number,
  phaseIdCurrent: string | number,
  releaseId: string | number
) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.MOVE_TO_PHASE_ALL_PHASE_TASKS });
      const response = await axios.post(taskBaseUrl + `MoveToPhaseAllPhaseTaskAsync`, null, {
        params: {
          projectId,
          phaseIdNew,
          phaseIdCurrent,
          releaseId,
        },
      });
      if (response.data.Code === StatusCode.Success) {
        dispatch({
          type: ProjectActions.MOVE_TO_PHASE_ALL_PHASE_TASKS_SUCCESS,
          payload: { phaseIdNew, phaseIdCurrent },
        });
      } else {
        dispatch({
          type: ProjectActions.MOVE_TO_PHASE_ALL_PHASE_TASKS_ERROR,
          payload: response.data.data.Error,
        });
      }
    } catch (e) {
      dispatch({
        type: ProjectActions.MOVE_TO_PHASE_ALL_PHASE_TASKS_ERROR,
        payload: `ERROR: Can't move all phase tasks.`,
      });
    }
  };
};

export const getReleasesAllPhaseTasks = (projectId: number | string) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.GET_RELEASES_ALL_PHASE_TASKS });
      const response = await axios.post(taskBaseUrl + `GetReleasesAllPhaseTasksAsync`, null, {
        params: {
          projectId,
        },
      });
      if (response.data.Code === StatusCode.Success) {
        dispatch({
          type: ProjectActions.GET_RELEASES_ALL_PHASE_TASKS_SUCCESS,
          payload: response.data.data,
        });
      } else {
        dispatch({
          type: ProjectActions.GET_RELEASES_ALL_PHASE_TASKS_ERROR,
          payload: response.data.data.Error,
        });
      }
    } catch (e) {
      dispatch({
        type: ProjectActions.GET_RELEASES_ALL_PHASE_TASKS_ERROR,
        payload: `ERROR: Can't get all project releases.`,
      });
    }
  };
};

export const getPhasesAllPhaseTasks = (projectId: number | string) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.GET_PHASES_ALL_PHASE_TASKS });
      const response = await axios.post(taskBaseUrl + `GetPhasesAllPhaseTasksAsync`, null, {
        params: {
          projectId,
        },
      });
      if (response.data.Code === StatusCode.Success) {
        dispatch({
          type: ProjectActions.GET_PHASES_ALL_PHASE_TASKS_SUCCESS,
          payload: response.data.data,
        });
      } else {
        dispatch({
          type: ProjectActions.GET_PHASES_ALL_PHASE_TASKS_ERROR,
          payload: response.data.data.Error,
        });
      }
    } catch (e) {
      dispatch({
        type: ProjectActions.GET_PHASES_ALL_PHASE_TASKS_ERROR,
        payload: `ERROR: Can't get all project phase.`,
      });
    }
  };
};

export const createProjectPhase = (input: ICreateProjectPhaseInput) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.CREATE_PROJECT_PHASE });
      const response = await axios.post(projectUrls.createProjectPhase, input);
      dispatch({
        type: ProjectActions.CREATE_PROJECT_PHASE_SUCCESS,
        payload: response.data.data,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.CREATE_PROJECT_PHASE_ERROR,
        payload: `ERROR: Can't create project phase.`,
      });
    }
  };
};

export const renamePhase = (input: IIdValueModel<number, string>) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.RENAME_PHASE });
      await axios.put(projectUrls.renameProjectPhase, input);
      dispatch({
        type: ProjectActions.RENAME_PHASE_SUCCESS,
        payload: input,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.ERROR,
        payload: `ERROR: Can't rename project phase.`,
      });
    }
  };
};

export const deletePhase = (phase: IIdModel<number, number>) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.DELETE_PHASE });
      await axios.put(projectUrls.deleteProjectPhase, phase);
      dispatch({
        type: ProjectActions.DELETE_PHASE_SUCCESS,
        payload: phase,
      });
    } catch (e) {
      dispatch({
        type: ProjectActions.DELETE_PHASE_ERROR,
        payload: `ERROR: Can't delete project phase.`,
      });
    }
  };
};

export const changeBoardSortingType = (sortingType: number) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({
        type: ProjectActions.SET_BOARD_SORTING_TYPE,
        payload: { sortingType },
      });
    } catch (e) {
      console.log(`ERROR: Can't change board sorting type.`);
    }
  };
};

export const changeVisibilityOfLanePhase = (data: {
  projectId: number;
  phaseId: number;
  visibility: FrontendVisibility;
}) => {
  return async (dispatch: Dispatch<ProjectAction>) => {
    try {
      dispatch({ type: ProjectActions.CHANGE_VISIBILITY_OF_LANE_PHASE });
      await axios.post(settingsViewFrontend.changeVisibilityPhase, data);
      dispatch({
        type: ProjectActions.CHANGE_VISIBILITY_OF_LANE_PHASE_SUCCESS,
        payload: { phaseId: data.phaseId, visibility: data.visibility },
      });
      return true;
    } catch (e) {
      dispatch({
        type: ProjectActions.CHANGE_VISIBILITY_OF_LANE_PHASE_ERROR,
        payload: `ERROR: Can't change state visibility of lane.`,
      });
      return false;
    }
  };
};
